// Shared helpers for the calendar's configurable data fields (calendar.fields).
// Used by PersonalInfoForm (rendering) and Scheduler (step validation).

// Fallback contact fields when a calendar has no fields.customer config.
export const DEFAULT_CONTACT_FIELDS = [
  { id: 'first_name', name: 'First Name', type: 'text', required: true, placeholder: 'Enter your first name' },
  { id: 'last_name', name: 'Last Name', type: 'text', required: true, placeholder: 'Enter your last name' },
  { id: 'phone', name: 'Phone Number', type: 'text', required: true, placeholder: '(555) 123-4567' },
  { id: 'email', name: 'Email Address', type: 'text', required: true, placeholder: 'your.email@example.com' },
];

// Fallback extra fields when a calendar has no fields.extra config.
export const DEFAULT_EXTRA_FIELDS = [
  {
    id: 'entity_data__comments',
    name: 'Comments',
    type: 'textarea',
    required: false,
    placeholder: 'Please share any additional information about your service needs...',
  },
];

// Per-possession-model fallback fields (mirror the builder defaults). Vehicle's
// year/make/model are the always-required core rendered via the Motor dropdowns.
export const DEFAULT_POSSESSION_FIELDS = {
  vehicle: [
    { id: 'year', name: 'Year', type: 'number', required: true, locked: true, placeholder: 'e.g., 2024' },
    { id: 'make', name: 'Make', type: 'text', required: true, locked: true },
    { id: 'model', name: 'Model', type: 'text', required: true, locked: true },
  ],
  animal: [
    { id: 'name', name: 'Name', type: 'text', required: false },
    { id: 'species', name: 'Species', type: 'text', required: false },
  ],
  real_estate: [
    { id: 'address1', name: 'Address 1', type: 'text', required: false },
    { id: 'address2', name: 'Address 2', type: 'text', required: false },
    { id: 'city', name: 'City', type: 'text', required: false },
    { id: 'state', name: 'State', type: 'text', required: false },
    { id: 'zip_code', name: 'Zipcode', type: 'text', required: false },
  ],
};

export const getContactFields = (fields) =>
  Array.isArray(fields?.customer) && fields.customer.length ? fields.customer : DEFAULT_CONTACT_FIELDS;

export const getExtraFields = (fields) =>
  Array.isArray(fields?.extra) && fields.extra.length ? fields.extra : DEFAULT_EXTRA_FIELDS;

// Which possession type this calendar collects: 'vehicle' | 'animal' | 'real_estate' | null.
export const getPossessionModel = (fields) => fields?.possession_model || null;

// The configured possession fields (falls back to the model's defaults).
export const getPossessionFields = (fields) => {
  const model = getPossessionModel(fields);
  if (!model) return [];
  if (Array.isArray(fields?.possession) && fields.possession.length) return fields.possession;
  return DEFAULT_POSSESSION_FIELDS[model] || [];
};

// Whether a field is required. THE single source of truth for "required" in the
// widget: the same `required` flag the calendar API returns on each field object
// (CalendarContext sets fields from data.fields). Both the Next-button advance
// gate (Scheduler.js isStepValid) and the visual required indicator (FieldLabel)
// read required-ness through this helper, so an indicator can never drift from
// the gate when a shop reconfigures its calendar.
export const isFieldRequired = (field) => !!field?.required;

// The personalInfo state key a field's value is stored under — its field id,
// with the entity_data__ prefix stripped for custom fields.
export const fieldStateKey = (field) => field.id.replace(/^entity_data__/, '');

// The HTML input type for a field's value.
export const fieldInputType = (field) => {
  if (field.id === 'email') return 'email';
  if (field.type === 'number') return 'number';
  if (field.type === 'date') return 'date';
  return 'text';
};

// Label with an "(Optional)" suffix appended when the field isn't required.
export const fieldLabel = (field) => `${field.name}${field.required ? '' : ' (Optional)'}`;
